import { resources } from "@constants/hardcoded-strings";
import { ButtonWithSpinner } from "@containers/common/button-with-spinner/button-with-spinner";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { OrganizationUserDetailsPreferenceContainer } from "@containers/organization/organization-user-details/organization-user-details-tab-container/organization-user-details-main-tab/organization-user-details-preference-container/organization-user-details-preference-container";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ButtonTypes } from "@iddataweb/idw-common-ui/enums";
import { SelectOption } from "@iddataweb/idw-common-ui/types";
import { RelyingPartyRole } from "@idw-types/common";
import { OrganizationUserDetailsMainValidationErrors } from "@idw-types/common/validation/organization/organization-user-details/organization-user-details-main-validation-errors";
import { AxnAdminAvailableUserProperty } from "@idw-types/data/axn-admin-available-user-property";
import { AxnAdminUser } from "@idw-types/data/axn-admin-user";
import { AxnAdminUserPropertyBundle } from "@idw-types/data/axn-admin-user-property-bundle";
import { AxnAdminUserProperty } from "@idw-types/data/axn-admin-user/axn-admin-user-property";
import { IDWSession } from "@idw-types/data/idw-session";
import { AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { Auth0IdentityProvider, IdentityProvider } from "@idw-types/data/reference-data/generic";
import { resetAxnAdminUserPasswordRequest } from "@requests/axnadmin-core/reset-axn-admin-user-password";
import { displaySuccessModal, logAndDisplayErrorModal } from "@utils/app-alert-util";
import { sortStrings } from "@utils/string-util";
import { ChangeEvent, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import styles from "./organization-user-details-main-tab.less";
import { OrganizationUserDetailsRoleContainer } from "./organization-user-details-role-container/organization-user-details-role-container";
import { isAxnAdminUser } from "@utils/permissions-util";
import { USER_PREFERENCES_PERMISSIONS } from "@constants/user-preferences";
import { UserPermissionTypes } from "@enums/user-permission-types";

type OrganizationUserDetailsMainTabProps = {
    isDisabled: boolean;
    canModifyUser: boolean;
    session: IDWSession;
    axnAdminUserState: AxnAdminUser;
    isCustomOrgRole: boolean;
    adminProperty: AxnAdminUserProperty;
    organizationRole: AxnAdminUserPropertyBundle;
    relyingPartyRoles: Array<RelyingPartyRole>;
    axnAdminUserPropertyBundles: Array<AxnAdminUserPropertyBundle>;
    axnAdminAvailableUserOrganizationProperties: Array<AxnAdminAvailableUserProperty>;
    axnAdminAvailableUserRPProperties: Array<AxnAdminAvailableUserProperty>;
    axnIdentityProviders: Array<IdentityProvider>;
    auth0IdentityProviders: Array<Auth0IdentityProvider>;
    axnRelyingParties: Array<AxnRelyingParty>;
    validationErrors: OrganizationUserDetailsMainValidationErrors;
    setAxnAdminUserState: (axnAdminUser: AxnAdminUser) => void;
};

export const OrganizationUserDetailsMainTab = (props: OrganizationUserDetailsMainTabProps) => {
    const {
        isDisabled,
        canModifyUser,
        session,
        axnAdminUserState,
        adminProperty,
        isCustomOrgRole,
        organizationRole,
        relyingPartyRoles,
        axnAdminUserPropertyBundles,
        axnRelyingParties,
        axnAdminAvailableUserOrganizationProperties,
        axnAdminAvailableUserRPProperties,
        axnIdentityProviders,
        auth0IdentityProviders,
        validationErrors,
        setAxnAdminUserState
    } = props;

    const [resetPasswordRequestInProgress, setResetPasswordRequestInProgress] = useState(false);
    const { axnAdminUserDetails } = axnAdminUserState;
    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);
    const dispatch = useDispatch();
    const handleFirstNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setAxnAdminUserState({
            ...axnAdminUserState,
            axnAdminUserDetails: {
                ...axnAdminUserDetails,
                firstName: event.target.value
            }
        });
    };

    const handleLastNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setAxnAdminUserState({
            ...axnAdminUserState,
            axnAdminUserDetails: {
                ...axnAdminUserDetails,
                lastName: event.target.value
            }
        });
    };

    const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
        setAxnAdminUserState({
            ...axnAdminUserState,
            axnAdminUserDetails: {
                ...axnAdminUserDetails,
                email: event.target.value
            }

        });
    };

    const handleIdentityProviderChange = (selectOption: SelectOption) => {
        setAxnAdminUserState({
            ...axnAdminUserState,
            axnAdminUserDetails: {
                ...axnAdminUserDetails,
                identityProviderID: selectOption.value
            }
        });
    };

    const handleChangeCredentials = (event: ChangeEvent<HTMLInputElement>) => {
        setAxnAdminUserState({
            ...axnAdminUserState,
            axnAdminUserDetails: {
                ...axnAdminUserDetails,
                credential: event.target.value
            }
        });
    };

    const handleResetUserPasswordClick = async () => {
        setResetPasswordRequestInProgress(true);
        const { data: resetPasswordData, error } = await resetAxnAdminUserPasswordRequest(axnAdminUserState.axnAdminUserDetails.email);
        if (resetPasswordData) {
            displaySuccessModal(
                dispatch,
                resources.requests.resetAxnAdminUserPassword.success.title,
                resources.requests.resetAxnAdminUserPassword.success.message
            );
        } else {
            if (error) {
                logAndDisplayErrorModal(
                    dispatch,
                    error,
                    resources.requests.resetAxnAdminUserPassword.error.title,
                    resources.requests.resetAxnAdminUserPassword.error.message,
                    error
                );

            }
        }

        setResetPasswordRequestInProgress(false);
    }

    const identityProviderOptions = useMemo(() => {
        return axnIdentityProviders
            .map(idp => {
                return {
                    label: idp.name,
                    value: idp.id
                }
            }).sort((a, b) => sortStrings(a.label, b.label));
    }, [axnIdentityProviders]);


    const selectedIdentityProviderOption = useMemo(() => {
        const option = identityProviderOptions.find(idp => idp.value === axnAdminUserDetails.identityProviderID);
        if (option) {
            return option;
        }
        return null;
    }, [identityProviderOptions, axnAdminUserDetails.identityProviderID]);

    const isAuth0IdentityProvider = useMemo(() => {
        if (selectedIdentityProviderOption) {
            return auth0IdentityProviders.map(idp => idp.id).includes(selectedIdentityProviderOption.value);
        }
        return false;

    }, [selectedIdentityProviderOption, auth0IdentityProviders]);

    const checkPerms = useMemo(() => {
        if (isAxnAdminUser(axnAdminUserState)) {
            return true;
        }
        else {
            if (axnAdminUserState.axnAdminUserOrganizationProperties.some((property) =>
                (USER_PREFERENCES_PERMISSIONS).includes(property.propertyName as UserPermissionTypes) && property.propertyValue === "true"
            )) {
                return true;
            }
            if (axnAdminUserState.axnAdminUserRPProperties.some((property) =>
                (USER_PREFERENCES_PERMISSIONS).includes(property.propertyName as UserPermissionTypes) && property.propertyValue === "true"
            )) {
                return true;
            }
        }
        return false;
    }, [axnAdminUserState, USER_PREFERENCES_PERMISSIONS]);

    const detailsResources = resources.pages.organizationUserDetails.tabs.details;

    return (
        <div>
            <ContentPageHeader title={detailsResources.title} />
            <ContentPageBody isMaxWidth={true}>
                <InputFormGroup
                    controlId="organization-user-first-name-input"
                    automationId={"organization-user-first-name-input"}
                    label={detailsResources.inputs.firstName.label}
                    ariaLabel={detailsResources.inputs.firstName.label}
                    value={axnAdminUserDetails.firstName}
                    disabled={isDisabled}
                    isRequired={true}
                    labelWidth={labelWidth}
                    placeholder={detailsResources.inputs.firstName.label}
                    onChange={handleFirstNameChange}
                />
                {validationErrors.firstNameError}

                <InputFormGroup
                    controlId="organization-user-last-name-input"
                    automationId={"organization-user-last-name-input"}
                    label={detailsResources.inputs.lastName.label}
                    ariaLabel={detailsResources.inputs.lastName.label}
                    value={axnAdminUserDetails.lastName}
                    disabled={isDisabled}
                    isRequired={true}
                    labelWidth={labelWidth}
                    placeholder={detailsResources.inputs.lastName.label}
                    onChange={handleLastNameChange}
                />
                {validationErrors.lastNameError}

                <InputFormGroup
                    controlId="organization-user-email-input"
                    automationId={"organization-user-email-input"}
                    label={detailsResources.inputs.email.label}
                    ariaLabel={detailsResources.inputs.email.label}
                    value={axnAdminUserDetails.email}
                    disabled={isDisabled}
                    isRequired={true}
                    labelWidth={labelWidth}
                    placeholder={detailsResources.inputs.email.placeholder}
                    onChange={handleEmailChange}
                />
                {validationErrors.emailError}

                <InputFormGroup
                    controlId="organization-user-credential-input"
                    automationId={"organization-user-credential-input"}
                    label={detailsResources.inputs.credential.label}
                    ariaLabel={detailsResources.inputs.credential.label}
                    value={axnAdminUserDetails.credential}
                    disabled={isDisabled}
                    isRequired={true}
                    labelWidth={labelWidth}
                    placeholder={detailsResources.inputs.credential.placeholder}
                    onChange={handleChangeCredentials}
                />

                {isAuth0IdentityProvider && canModifyUser && axnAdminUserDetails.email && axnAdminUserDetails.credential &&
                    <div className={styles.optionsContainer}>
                        <ButtonWithSpinner
                            id="organization-user-details-reset-password-button"
                            type={ButtonTypes.BUTTON}
                            variant="primary"
                            label={detailsResources.buttons.resetPassword.label}
                            onClick={handleResetUserPasswordClick}
                            disabled={resetPasswordRequestInProgress}
                            hideSpinner={!resetPasswordRequestInProgress}
                        />
                    </div>
                }
                <OrganizationUserDetailsRoleContainer
                    session={session}
                    axnAdminUserState={axnAdminUserState}
                    isCustomOrgRole={isCustomOrgRole}
                    adminProperty={adminProperty}
                    organizationRole={organizationRole}
                    relyingPartyRoles={relyingPartyRoles}
                    axnAdminAvailableUserOrganizationProperties={axnAdminAvailableUserOrganizationProperties}
                    axnAdminAvailableUserRPProperties={axnAdminAvailableUserRPProperties}
                    axnAdminUserPropertyBundles={axnAdminUserPropertyBundles}
                    axnRelyingParties={axnRelyingParties}
                    isDisabled={isDisabled}
                    labelWidth={labelWidth}
                    setAxnAdminUserState={setAxnAdminUserState}
                />
                {validationErrors.relyingPartyError}
                <SelectFormGroup
                    controlId={"organization-user-identity-provider"}
                    automationId={"organization-user-identity-provider"}
                    label={detailsResources.inputs.identityProvider.label}
                    ariaLabel={detailsResources.inputs.identityProvider.label}
                    disabled={isDisabled || !axnAdminUserState.permissions.canModifyIDP}
                    isClearable={false}
                    isRequired={true}
                    isSearchable={true}
                    labelWidth={labelWidth}
                    onChange={handleIdentityProviderChange}
                    options={identityProviderOptions}
                    selectedOption={selectedIdentityProviderOption}
                />
                {validationErrors.identityProviderError}
            </ContentPageBody>
            {checkPerms &&
                <OrganizationUserDetailsPreferenceContainer
                    axnAdminUserState={axnAdminUserState}
                    setAxnAdminUserState={setAxnAdminUserState}
                    isDisabled={isDisabled}
                    labelWidth={LabelWidthTypes.LARGE}
                />
            }
        </div>
    )
}
